import axios from "axios";
import { useCallback, useEffect, useState } from "react";

export const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

let pinPrompt = null;
export const registerSuperPinPrompt = (fn) => { pinPrompt = fn; };
let permGuard = null;
export const setPermGuard = (fn) => { permGuard = fn; };

const ACTION_LABELS = [
  [/^\/admin\/members\/[^/]+\/pin$/, "Atur ulang PIN anggota"],
  [/^\/admin\/members\/[^/]+$/, { patch: "Edit data anggota", delete: "Hapus anggota" }],
  [/^\/admin\/members$/, "Tambah anggota"],
  [/^\/admin\/registrations\/[^/]+\/approve$/, "Setujui pendaftaran"],
  [/^\/admin\/registrations\/[^/]+\/reject$/, "Tolak pendaftaran"],
  [/^\/admin\/registrations\/[^/]+$/, "Hapus pendaftaran"],
  [/^\/admin\/savings$/, "Catat transaksi simpanan"],
  [/^\/admin\/loans\//, "Keputusan pinjaman"],
  [/^\/admin\/requests\//, "Verifikasi pembayaran/transfer"],
  [/^\/admin\/settings$/, "Ubah pengaturan CU"],
  [/^\/admin\/permissions$/, "Ubah hak sesuai jabatan"],
  [/^\/admin\/fees$/, "Ubah biaya layanan transfer"],
];
const actionLabel = (cfg) => {
  const hit = ACTION_LABELS.find(([re]) => re.test(cfg.url));
  const label = hit?.[1];
  return typeof label === "object" ? label[cfg.method] : label || "Perubahan data";
};
const needsPin = (cfg) => ["post", "put", "patch", "delete"].includes(cfg.method) && cfg.url?.startsWith("/admin/");

let pinQueue = Promise.resolve();
const askPin = (cfg, error) => {
  const run = pinQueue.then(async () => {
    const pin = pinPrompt ? await pinPrompt({ action: actionLabel(cfg), error }) : null;
    if (!pin) {
      const err = new Error("canceled");
      err.canceled = true;
      err.response = { data: { detail: "Perubahan dibatalkan" } };
      throw err;
    }
    cfg.headers["X-Approval-Pin"] = pin;
    return cfg;
  });
  pinQueue = run.catch(() => {});
  return run;
};

api.interceptors.request.use((cfg) => {
  if (!needsPin(cfg) || cfg.headers["X-Approval-Pin"]) return cfg;
  const denied = permGuard?.(cfg);
  if (denied) {
    const err = new Error("forbidden");
    err.response = { status: 403, data: { detail: denied } };
    return Promise.reject(err);
  }
  return askPin(cfg);
});
api.interceptors.response.use((res) => {
  if (needsPin(res.config)) window.dispatchEvent(new Event("cu-admin-changed"));
  return res;
}, async (err) => {
  const cfg = err.config;
  const code = err?.response?.data?.detail?.code;
  if (cfg && needsPin(cfg) && code === "approval_pin_invalid") {
    await askPin(cfg, err.response.data.detail.message);
    return api.request(cfg);
  }
  return Promise.reject(err);
});

export const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d[0]?.msg || "Data tidak valid";
  if (d && typeof d === "object") return d.message || "Terjadi kesalahan";
  return d || "Terjadi kesalahan";
};

export function useApi(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    if (!path) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await api.get(path);
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [path]);
  useEffect(() => { reload(); }, [reload]);
  return { data, loading, reload };
}
